// Canonical default shape for a deal-calc Deal — TypeScript port of
// `listblox/deals/defaults.py`. Field order, weights, and amounts must
// match the Python module exactly; the math contract assumes both ports
// produce identical defaults when a field is missing from saved state.

export type DealMode = 'reno' | 'build';
export type DealSource = 'standalone' | 'listblox-parcel';

export interface IDealLineItem {
	id: string;
	label: string;
	amount: number;
	weight: number;
}

export interface IDealHoldingItem {
	id: string;
	label: string;
	monthly: number;
}

export interface IDealProperty {
	address: string;
	listingUrl: string;
	sqft: number;
	beds: number;
	baths: number;
	yearBuilt: number;
	lotAcres: number;
}

export interface IDealState {
	property: IDealProperty;
	mode: DealMode;
	purchasePrice: number;
	listPrice: number;
	priceReductionPct: number;
	closingCosts: Array<IDealLineItem>;
	renoTotal: number;
	renoItems: Array<IDealLineItem>;
	contingencyPct: number;
	buildCostPerSqft: number;
	buildCostTotal: number;
	enclosedSqft: number;
	enclosedCostPerSqft: number;
	enclosedValuePerSqft: number;
	enclosedCostInReno: boolean;
	marketingMultiplier: number;
	valueSyncedToPPSF: boolean;
	holdMonths: number;
	holdingItems: Array<IDealHoldingItem>;
	financedAmount: number;
	financedRatePct: number;
	baseARV: number;
	isFlip: boolean;
	sellingCosts: Array<IDealLineItem>;
	source: DealSource;
	parcelId: string | null;
	parcelSnapshot: Record<string, any> | null;
}

export const DEFAULT_RENO_LINE_ITEMS: ReadonlyArray<IDealLineItem> = [
	{id: 'demo', label: 'Demo & disposal', amount: 0, weight: 0.04},
	{id: 'roof', label: 'Roof', amount: 0, weight: 0.08},
	{id: 'hvac', label: 'HVAC', amount: 0, weight: 0.07},
	{id: 'plumbing', label: 'Plumbing', amount: 0, weight: 0.06},
	{id: 'electrical', label: 'Electrical', amount: 0, weight: 0.06},
	{id: 'windows', label: 'Windows & doors', amount: 0, weight: 0.06},
	{id: 'kitchen', label: 'Kitchen', amount: 0, weight: 0.15},
	{id: 'bathrooms', label: 'Bathrooms', amount: 0, weight: 0.12},
	{id: 'flooring', label: 'Flooring', amount: 0, weight: 0.08},
	{id: 'paint_int', label: 'Interior paint & drywall', amount: 0, weight: 0.06},
	{id: 'paint_ext', label: 'Exterior paint & siding', amount: 0, weight: 0.05},
	{id: 'landscaping', label: 'Landscaping & curb appeal', amount: 0, weight: 0.04},
	{id: 'permits', label: 'Permits & inspections', amount: 0, weight: 0.03},
	{id: 'misc', label: 'Misc / finishes', amount: 0, weight: 0.10},
];

export const DEFAULT_BUILD_LINE_ITEMS: ReadonlyArray<IDealLineItem> = [
	{id: 'site_work', label: 'Site work / excavation / foundation', amount: 0, weight: 0.11},
	{id: 'framing', label: 'Framing & rough carpentry', amount: 0, weight: 0.13},
	{id: 'roofing', label: 'Roofing', amount: 0, weight: 0.06},
	{id: 'exterior', label: 'Exterior (siding, windows, doors)', amount: 0, weight: 0.11},
	{id: 'mep_rough', label: 'MEP rough-in (mech/elec/plumbing)', amount: 0, weight: 0.13},
	{id: 'insul_drywall', label: 'Insulation & drywall', amount: 0, weight: 0.06},
	{id: 'finishes', label: 'Interior finishes (flooring/paint/trim)', amount: 0, weight: 0.09},
	{id: 'kitchen', label: 'Kitchen', amount: 0, weight: 0.07},
	{id: 'bathrooms', label: 'Bathrooms', amount: 0, weight: 0.07},
	{id: 'cabinetry', label: 'Cabinetry & built-ins', amount: 0, weight: 0.04},
	{id: 'permits_fees', label: 'Permits & impact fees', amount: 0, weight: 0.04},
	{id: 'site_finish', label: 'Site finish (landscape, driveway)', amount: 0, weight: 0.04},
	{id: 'gen_conditions', label: 'General conditions / supervision', amount: 0, weight: 0.05},
];

export const DEFAULT_CLOSING_COSTS: ReadonlyArray<IDealLineItem> = [
	{id: 'title', label: 'Title insurance', amount: 0, weight: 0.005},
	{id: 'escrow', label: 'Escrow & settlement', amount: 0, weight: 0.003},
	{id: 'inspection', label: 'Inspection', amount: 500, weight: 0},
	{id: 'appraisal', label: 'Appraisal', amount: 600, weight: 0},
	{id: 'recording', label: 'Recording & transfer', amount: 0, weight: 0.005},
	{id: 'lender', label: 'Lender fees / points', amount: 0, weight: 0},
	{id: 'other_acq', label: 'Other acquisition', amount: 0, weight: 0},
];

export const DEFAULT_SELLING_COSTS: ReadonlyArray<IDealLineItem> = [
	{id: 'listing_agent', label: 'Listing agent commission', amount: 0, weight: 0.03},
	{id: 'buyer_agent', label: "Buyer's agent commission", amount: 0, weight: 0.03},
	{id: 'concessions', label: 'Seller concessions', amount: 0, weight: 0.01},
	{id: 'title_sell', label: 'Title / escrow / transfer', amount: 0, weight: 0.01},
];

export const DEFAULT_HOLDING_MONTHLY: ReadonlyArray<IDealHoldingItem> = [
	{id: 'mortgage', label: 'Mortgage / loan interest', monthly: 0},
	{id: 'property_tax', label: 'Property tax', monthly: 0},
	{id: 'insurance', label: 'Insurance', monthly: 0},
	{id: 'utilities', label: 'Utilities', monthly: 150},
	{id: 'hoa', label: 'HOA', monthly: 0},
];

function blankProperty(): IDealProperty {
	return {
		address: '',
		listingUrl: '',
		sqft: 0,
		beds: 0,
		baths: 0,
		yearBuilt: 0,
		lotAcres: 0,
	};
}

export function createBlankDealState(): IDealState {
	return {
		property: blankProperty(),
		mode: 'reno',
		purchasePrice: 0,
		listPrice: 0,
		priceReductionPct: 0,
		closingCosts: DEFAULT_CLOSING_COSTS.map(c => ({...c})),
		renoTotal: 0,
		renoItems: DEFAULT_RENO_LINE_ITEMS.map(c => ({...c})),
		contingencyPct: 10,
		buildCostPerSqft: 400,
		buildCostTotal: 0,
		enclosedSqft: 0,
		enclosedCostPerSqft: 85,
		enclosedValuePerSqft: 200,
		enclosedCostInReno: true,
		marketingMultiplier: 0,
		valueSyncedToPPSF: true,
		holdMonths: 6,
		holdingItems: DEFAULT_HOLDING_MONTHLY.map(c => ({...c})),
		financedAmount: 0,
		financedRatePct: 0,
		baseARV: 0,
		isFlip: true,
		sellingCosts: DEFAULT_SELLING_COSTS.map(c => ({...c})),
		source: 'standalone',
		parcelId: null,
		parcelSnapshot: null,
	};
}

// Storage-contract migration-merge. Old stored deals come back hydrated
// against a fresh template so any field added since the row was last
// written gets a sensible default with no per-field migration code.
export function mergeStateWithTemplate(state: Partial<IDealState> | null | undefined): IDealState {
	const template = createBlankDealState();
	const stored = (state && typeof state === 'object') ? state : {};
	const merged: IDealState = {
		...template,
		...stored,
		property: {
			...template.property,
			...((stored as any).property || {}),
		},
	};
	return merged;
}
